import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import DonateModal from "./DonateModal";

const PLATFORMS = [
  { name: "Spotify", color: "#1DB954", url: "" },
  { name: "Apple Podcasts", color: "#A855F7", url: "" },
  { name: "YouTube", color: "#FF0000", url: "https://www.youtube.com/@dennishaymon4027" },
  { name: "iHeartRadio", color: "#C6002B", url: "" },
].filter((p) => p.url);

export default function Subscribe() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await api.post("/subscribe", { email: email.trim() });
      toast.success(res.data.message);
      setDone(true);
      setEmail("");
    } catch {
      toast.error("Please enter a valid email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="subscribe" className="relative py-24 bg-[#0066FF] overflow-hidden" data-testid="subscribe-section">
      <div className="absolute inset-0 grain opacity-40" />
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#FF6B00]/30 blur-3xl" />
      <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-display text-4xl sm:text-6xl uppercase leading-none text-white"
        >
          Join The Conversation
        </motion.h2>
        <p className="mt-4 text-white/85 text-lg max-w-xl mx-auto">
          Be part of the change. Get new episodes weekly, plus behind-the-scenes stories from St. Louis.
        </p>

        <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" data-testid="subscribe-form">
          <div className="relative flex-1">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#050B14]/50" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="subscribe-email-input"
              className="w-full rounded-full bg-white pl-12 pr-4 py-4 text-[#050B14] outline-none placeholder:text-[#050B14]/50"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="subscribe-submit-button"
            className="rounded-full bg-[#050B14] hover:bg-[#0C1424] disabled:opacity-70 transition-colors px-8 py-4 font-cond font-bold uppercase tracking-wide text-white flex items-center justify-center gap-2"
          >
            {done ? <><Check className="h-5 w-5" /> Subscribed</> : loading ? "..." : "Subscribe"}
          </button>
        </form>

        <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3 text-white/90">
          <span className="font-cond uppercase tracking-wide text-sm">Or support the show directly</span>
          <DonateModal variant="dark" testid="subscribe-donate-btn" />
        </div>

        <div className="mt-10">
          <div className="font-mono2 text-xs tracking-[0.3em] uppercase text-white/70 mb-4">Also listen on</div>
          <div className="flex flex-wrap justify-center gap-3">
            {PLATFORMS.map((p) => (
              <a
                key={p.name}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`platform-${p.name.toLowerCase().replace(" ", "-")}`}
                className="flex items-center gap-2 rounded-full bg-[#050B14]/85 hover:bg-[#050B14] transition-colors px-5 py-2.5 font-cond font-semibold uppercase text-sm text-white"
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />
                {p.name}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
